import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const AUTO_SOURCES: Record<string, () => Promise<string>> = {
  projects_count: async () => {
    const { count } = await supabase.from("projects").select("*", { count: "exact", head: true });
    return formatNumber(count ?? 0);
  },
  completed_projects_count: async () => {
    const { count } = await supabase
      .from("projects")
      .select("*", { count: "exact", head: true })
      .eq("status", "completed");
    return formatNumber(count ?? 0);
  },
  raised_total: async () => {
    const { data } = await supabase.from("projects").select("raised_cents");
    const total = (data ?? []).reduce((acc, p: any) => acc + (p.raised_cents ?? 0), 0);
    return `$${formatNumber(Math.round(total / 100))}`;
  },
  donors_count: async () => {
    const { data } = await supabase
      .from("donations")
      .select("donor_email")
      .eq("status", "succeeded");
    const set = new Set((data ?? []).map((d: any) => d.donor_email).filter(Boolean));
    return formatNumber(set.size);
  },
  fundraisers_count: async () => {
    const { count } = await supabase.from("fundraisers").select("*", { count: "exact", head: true });
    return formatNumber(count ?? 0);
  },
};

function formatNumber(n: number) {
  return new Intl.NumberFormat("en-US").format(n);
}

export function ImpactStats() {
  const { data: stats } = useQuery({
    queryKey: ["impact_stats_resolved"],
    queryFn: async () => {
      const { data } = await supabase
        .from("impact_stats")
        .select("*")
        .order("sort_order");
      const rows = data ?? [];
      const resolved = await Promise.all(
        rows.map(async (s: any) => {
          if (s.mode === "auto" && s.auto_source && AUTO_SOURCES[s.auto_source]) {
            try {
              const value = await AUTO_SOURCES[s.auto_source]();
              return { ...s, value };
            } catch {
              return s;
            }
          }
          return s;
        }),
      );
      return resolved;
    },
  });

  const { data: settings } = useQuery({
    queryKey: ["site_settings"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("key,value");
      return Object.fromEntries((data ?? []).map((r) => [r.key, r.value]));
    },
  });

  const mission =
    settings?.mission_statement ??
    "One family at a time. One village at a time. That's how we got here.";

  // Split into a short headline + supporting paragraph when copy is long.
  const sentences = mission.match(/[^.!?]+[.!?]+/g)?.map((s) => s.trim()) ?? [mission];
  const headline = sentences[0] ?? mission;
  const supporting = sentences.slice(1).join(" ").trim();

  return (
    <section className="bg-muted/40 py-20">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-primary">
            Our impact
          </span>
          <h2 className="mt-3 font-display text-3xl font-semibold leading-tight md:text-4xl lg:text-5xl text-balance">
            {headline}
          </h2>
          {supporting && (
            <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg text-pretty">
              {supporting}
            </p>
          )}
        </div>

        <div className="mt-14 grid grid-cols-2 gap-x-4 gap-y-10 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-6 lg:gap-x-4">
          {stats?.map((s: any) => (
            <div key={s.id} className="flex min-w-0 flex-col items-center text-center">
              <div
                className="font-display font-semibold leading-none text-primary whitespace-nowrap"
                style={{ fontSize: "clamp(1.5rem, 6vw, 2.75rem)" }}
              >
                {s.value}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const IMPACT_AUTO_SOURCES = [
  { value: "projects_count", label: "Total projects (count)" },
  { value: "completed_projects_count", label: "Completed projects (count)" },
  { value: "raised_total", label: "Total raised (USD)" },
  { value: "donors_count", label: "Unique donors (count)" },
  { value: "fundraisers_count", label: "Total fundraisers (count)" },
];
